import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  AssetAssignmentView, AssetCategoryView, AssetDisposalView, AssetMaintenanceView,
  AssetRecoveryView, AssetStocktakeLineView, AssetStocktakeView, AssetSummaryView, AssetView,
  LicenceCheckView,
} from '../types'

const BASE = '/v1/assets'
const STOCKTAKES = '/v1/asset-stocktakes'

export interface AssetListParams {
  page?: number
  size?: number
  sort?: string
  q?: string
  categoryId?: string
  status?: string
  locationId?: string
}

/** The register. Server-side paged: a register of a few thousand rows is normal. */
export function useAssets(params: AssetListParams) {
  return useQuery({
    queryKey: ['assets', 'list', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<AssetView>>(BASE, {
        params: {
          page: params.page,
          size: params.size,
          sort: params.sort,
          q: params.q || undefined,
          categoryId: params.categoryId || undefined,
          status: params.status || undefined,
          locationId: params.locationId || undefined,
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}

export function useAsset(id: string | undefined) {
  return useQuery({
    queryKey: ['assets', 'one', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<AssetView>(`${BASE}/${id}`)).data,
  })
}

export function useAssetSummary() {
  return useQuery({
    queryKey: ['assets', 'summary'],
    queryFn: async () => (await apiClient.get<AssetSummaryView>(`${BASE}/summary`)).data,
  })
}

export function useAssetCategories() {
  return useQuery({
    queryKey: ['assets', 'categories'],
    queryFn: async () => (await apiClient.get<AssetCategoryView[]>(`${BASE}/categories`)).data,
  })
}

export function useAssetAssignments(assetId: string | undefined) {
  return useQuery({
    queryKey: ['assets', 'assignments', assetId],
    enabled: Boolean(assetId),
    queryFn: async () =>
      (await apiClient.get<AssetAssignmentView[]>(`${BASE}/${assetId}/assignments`)).data,
  })
}

export function useAssetMaintenance(assetId: string | undefined) {
  return useQuery({
    queryKey: ['assets', 'maintenance', assetId],
    enabled: Boolean(assetId),
    queryFn: async () =>
      (await apiClient.get<AssetMaintenanceView[]>(`${BASE}/${assetId}/maintenance`)).data,
  })
}

export function useMaintenanceDue() {
  return useQuery({
    queryKey: ['assets', 'maintenance', 'due'],
    queryFn: async () =>
      (await apiClient.get<AssetMaintenanceView[]>(`${BASE}/maintenance/due`)).data,
  })
}

export function useAssetDisposals() {
  return useQuery({
    queryKey: ['assets', 'disposals'],
    queryFn: async () => (await apiClient.get<AssetDisposalView[]>(`${BASE}/disposals`)).data,
  })
}

/** What one person holds — the list an exit clearance is built from. */
export function useAssetsHeldBy(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['assets', 'held-by', employeeId],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<AssetAssignmentView[]>(`${BASE}/held-by/${employeeId}`)).data,
  })
}

export function useAssetRecovery(employeeId: string | undefined, valuation?: string) {
  return useQuery({
    queryKey: ['assets', 'recovery', employeeId, valuation],
    enabled: Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<AssetRecoveryView>(`${BASE}/recovery/${employeeId}`, {
        params: { valuation: valuation || undefined },
      })).data,
  })
}

/** What the licence check would find, before anybody presses anything. */
export function useLicenceCheck(assetId: string | undefined, employeeId: string | undefined) {
  return useQuery({
    queryKey: ['assets', 'licence-check', assetId, employeeId],
    enabled: Boolean(assetId) && Boolean(employeeId),
    queryFn: async () =>
      (await apiClient.get<LicenceCheckView>(`${BASE}/${assetId}/licence-check`, {
        params: { employeeId },
      })).data,
  })
}

export function useStocktakes() {
  return useQuery({
    queryKey: ['assets', 'stocktakes'],
    queryFn: async () => (await apiClient.get<AssetStocktakeView[]>(STOCKTAKES)).data,
  })
}

export function useStocktakeLines(stocktakeId: string | undefined) {
  return useQuery({
    queryKey: ['assets', 'stocktakes', stocktakeId, 'lines'],
    enabled: Boolean(stocktakeId),
    queryFn: async () =>
      (await apiClient.get<AssetStocktakeLineView[]>(`${STOCKTAKES}/${stocktakeId}/lines`)).data,
  })
}

export function useAssetMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['assets'] })
    // A settlement's asset-recovery line reads the same assignments.
    void queryClient.invalidateQueries({ queryKey: ['settlements'] })
  }

  return {
    saveCategory: useMutation({
      mutationFn: async ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
        id
          ? (await apiClient.put<AssetCategoryView>(`${BASE}/categories/${id}`, body)).data
          : (await apiClient.post<AssetCategoryView>(`${BASE}/categories`, body)).data,
      onSuccess: invalidate,
    }),
    /**
     * Retires a category.
     *
     * <p>Refused while assets are still filed under it: a register whose categories no longer
     * exist cannot be reported on.
     */
    retireCategory: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<AssetCategoryView>(`${BASE}/categories/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    saveAsset: useMutation({
      mutationFn: async ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
        id
          ? (await apiClient.put<AssetView>(`${BASE}/${id}`, body)).data
          : (await apiClient.post<AssetView>(BASE, body)).data,
      onSuccess: invalidate,
    }),
    assign: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<AssetAssignmentView>(`${BASE}/${id}/assign`, body)).data,
      onSuccess: invalidate,
    }),
    returnAsset: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<AssetAssignmentView>(
          `${BASE}/assignments/${id}/return`, body)).data,
      onSuccess: invalidate,
    }),
    markLost: useMutation({
      mutationFn: async ({ id, note }: { id: string; note: string }) =>
        (await apiClient.post<AssetAssignmentView>(
          `${BASE}/assignments/${id}/lost`, { note })).data,
      onSuccess: invalidate,
    }),
    recordMaintenance: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<AssetMaintenanceView>(`${BASE}/${id}/maintenance`, body)).data,
      onSuccess: invalidate,
    }),
    dispose: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<AssetDisposalView>(`${BASE}/${id}/dispose`, body)).data,
      onSuccess: invalidate,
    }),
    openStocktake: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<AssetStocktakeView>(STOCKTAKES, body)).data,
      onSuccess: invalidate,
    }),
    count: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<AssetStocktakeLineView>(`${STOCKTAKES}/${id}/count`, body)).data,
      onSuccess: invalidate,
    }),
    closeStocktake: useMutation({
      mutationFn: async ({ id, closedOn }: { id: string; closedOn: string }) =>
        (await apiClient.post<AssetStocktakeView>(`${STOCKTAKES}/${id}/close`, { closedOn })).data,
      onSuccess: invalidate,
    }),
  }
}
